// Lib
import React, { useCallback, useEffect, useState } from 'react';
import type { MutableRefObject } from 'react';
import { CursorAnchoredMenu } from '@milanote/roma/application';

// Utils
import { hideMenu as hideLegacyContextMenu } from '../../../../../../node_module_clones/react-contextmenu/src/actions';
import Mousetrap from '../../../../../workspace/shortcuts/Mousetrap';

// Components
import TableContextMenuV2 from './TableContextMenuV2';
import useTableContextMenuActions, {
    useTableContextMenuKeyboardHandling,
} from '../../../bridge/handlers/contextMenu/useTableContextMenuActions';

// Types
import type { MNHotInstance } from '../../../bridge/tableHotInstanceTypes';

type TableContextMenuManagerV2Props = {
    children: React.ReactNode;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    locale: string;
    elementId: string;
    dismissWhen: boolean;
};

/**
 * Opens the Roma table context menu over a table cell selection, replacing Handsontable's built-in
 * ContextMenu plugin (`contextMenu: false`). Right-clicks without a cell selection fall through to the
 * element context menu route, matching the seam ContextMenuExtensionHotPlugin provides for the HOT menu.
 */
const TableContextMenuManagerV2: React.FC<TableContextMenuManagerV2Props> = ({
    children,
    hotTableInstanceRef,
    locale,
    elementId,
    dismissWhen,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const getHotInstance = useCallback(() => hotTableInstanceRef.current, [hotTableInstanceRef]);
    useTableContextMenuKeyboardHandling(getHotInstance, isOpen);

    const shouldOpen = useCallback((): boolean => {
        if (dismissWhen) return false;

        const hasCellSelection = !!hotTableInstanceRef.current?.getSelected()?.length;
        if (!hasCellSelection) return false;

        hideLegacyContextMenu();
        return true;
    }, [dismissWhen, hotTableInstanceRef]);

    const onOpenChange = useCallback((isOpen: boolean) => setIsOpen(isOpen), []);

    // Resume workspace shortcuts if the menu manager unmounts while the menu is open.
    useEffect(() => {
        if (!isOpen) return;

        Mousetrap.pause();
        return () => Mousetrap.resume();
    }, [isOpen]);

    const { runGridAction, runClipboardAction } = useTableContextMenuActions({ getHotInstance });

    return (
        <CursorAnchoredMenu
            className="contents"
            shouldOpen={shouldOpen}
            onOpenChange={onOpenChange}
            dismissWhen={dismissWhen}
            menu={
                <TableContextMenuV2
                    hotTableInstanceRef={hotTableInstanceRef}
                    locale={locale}
                    elementId={elementId}
                    runGridAction={runGridAction}
                    runClipboardAction={runClipboardAction}
                />
            }
        >
            {children}
        </CursorAnchoredMenu>
    );
};

export default TableContextMenuManagerV2;
